import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Database, Download, Trash2, RefreshCw, CloudUpload, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBackup = () => {
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoreDialog, setRestoreDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [downloadReminderDialog, setDownloadReminderDialog] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [backupsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/backups/list`),
        axios.get(`${API_URL}/api/admin/backups/stats`)
      ]);
      setBackups(backupsRes.data.backups);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
      console.error('Backup data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      // Start backup (returns immediately)
      const response = await axios.post(`${API_URL}/api/admin/backups/create`, {}, {
        timeout: 10000  // 10 seconds - just to start
      });
      
      if (response.data.success) {
        toast.success('Yedekleme başlatıldı...');
        
        // Poll for completion
        let attempts = 0;
        const maxAttempts = 30; // 30 seconds max
        
        const checkStatus = async () => {
          try {
            const statusRes = await axios.get(`${API_URL}/api/admin/backups/status`);
            
            if (!statusRes.data.in_progress) {
              // Backup completed
              if (statusRes.data.last_error) {
                toast.error(`Yedekleme hatası: ${statusRes.data.last_error}`);
              } else {
                toast.success('Yedekleme başarıyla tamamlandı!');
                // Show download reminder popup
                setDownloadReminderDialog(statusRes.data.last_backup_name);
              }
              setCreating(false);
              fetchData();
              return;
            }
            
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(checkStatus, 1000);
            } else {
              toast.info('Yedekleme devam ediyor... Sayfa yenilenince görünecek.');
              setCreating(false);
              fetchData();
            }
          } catch (err) {
            console.error('Status check error:', err);
            setCreating(false);
          }
        };
        
        // Start polling after 1 second
        setTimeout(checkStatus, 1000);
      } else {
        toast.error(response.data.message || 'Yedekleme başlatılamadı');
        setCreating(false);
      }
    } catch (error) {
      console.error('Create backup error:', error);
      let errorMsg = 'Yedekleme başlatılamadı';
      if (error.code === 'ECONNABORTED') {
        errorMsg = 'Bağlantı zaman aşımı. Lütfen tekrar deneyin.';
      } else if (error.message === 'Network Error') {
        errorMsg = 'Sunucuya ulaşılamıyor. İnternet bağlantınızı kontrol edin.';
      } else {
        errorMsg = error.response?.data?.detail || error.message || errorMsg;
      }
      toast.error(errorMsg);
      setCreating(false);
    }
  };

  const handleRestore = async (backupId) => {
    try {
      // Use longer timeout for restore (120 seconds)
      await axios.post(`${API_URL}/api/admin/backups/restore/${backupId}`, {}, {
        timeout: 120000  // 120 seconds
      });
      toast.success('Veritabanı başarıyla geri yüklendi!');
      setRestoreDialog(null);
    } catch (error) {
      let errorMsg = 'Geri yükleme başarısız';
      if (error.code === 'ECONNABORTED') {
        errorMsg = 'Zaman aşımı - Geri yükleme çok uzun sürdü.';
      } else if (error.message === 'Network Error') {
        errorMsg = 'Bağlantı hatası - Sunucuya ulaşılamıyor.';
      }
      toast.error(errorMsg);
      console.error('Restore error:', error);
    }
  };

  const handleDelete = async (backupId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/backups/delete/${backupId}`);
      toast.success('Yedek silindi');
      setDeleteDialog(null);
      fetchData();
    } catch (error) {
      toast.error('Yedek silinemedi');
      console.error('Delete error:', error);
    }
  };

  const handleDownload = async (backupId) => {
    try {
      toast.info('Yedek hazırlanıyor...');
      const response = await axios.get(`${API_URL}/api/admin/backups/download/${backupId}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${backupId}.tar.gz`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Yedek indirme başladı!');
    } catch (error) {
      toast.error('Yedek indirilemedi');
      console.error('Download error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark py-6">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center py-12">
            <div className="h-12 w-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-dark py-6">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white mb-1">Yedekleme Yönetimi</h1>
            <p className="text-sm text-slate-400">MongoDB veritabanı yedekleme ve geri yükleme</p>
          </div>
          <div className="flex items-center space-x-2">
            <Button onClick={fetchData} variant="outline" size="sm" className="text-white border-white/10">
              <RefreshCw className="h-3 w-3 mr-2" />
              Yenile
            </Button>
            <Button onClick={handleCreateBackup} disabled={creating} size="sm" className="btn-primary">
              {creating ? (
                <>
                  <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Oluşturuluyor...
                </>
              ) : (
                <>
                  <CloudUpload className="h-3 w-3 mr-2" />
                  Yeni Yedek Oluştur
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <Card className="border-white/10 bg-slate-900/50 backdrop-blur">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400">Toplam Yedek</p>
                    <p className="text-2xl font-bold text-white">{stats.total_backups}</p>
                  </div>
                  <div className="bg-primary/20 p-2 rounded-lg">
                    <Database className="h-5 w-5 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-white/10 bg-slate-900/50 backdrop-blur">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400">Toplam Boyut</p>
                    <p className="text-2xl font-bold text-white">{stats.total_size}</p>
                  </div>
                  <div className="bg-blue-500/20 p-2 rounded-lg">
                    <Download className="h-5 w-5 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-white/10 bg-slate-900/50 backdrop-blur">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400">Son Yedek</p>
                    <p className="text-sm font-semibold text-white">
                      {stats.last_backup || 'Henüz yok'}
                    </p>
                  </div>
                  <div className="bg-green-500/20 p-2 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Important Notice */}
        <Card className="border-yellow-500/40 bg-yellow-900/10 mb-5">
          <CardContent className="p-3">
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="space-y-1 text-xs">
                <p className="text-yellow-300 font-medium">Önemli Notlar:</p>
                <ul className="text-yellow-300/80 list-disc list-inside space-y-0.5">
                  <li>Geri yükleme işlemi mevcut veritabanını tamamen değiştirir</li>
                  <li>Kritik işlemlerden önce yedek oluşturun</li>
                  <li>Yedekler /app/backups klasöründe saklanır</li>
                  <li>GitHub'a kod push'u için "Save to GitHub" özelliğini kullanın</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Backups List */}
        <Card className="border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white">Mevcut Yedekler</CardTitle>
            <CardDescription className="text-xs text-slate-400">
              Oluşturulan yedekleri görüntüleyin ve yönetin
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {backups.length === 0 ? (
              <div className="text-center py-8">
                <Database className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 mb-2">Henüz yedek oluşturulmamış</p>
                <p className="text-xs text-slate-500">
                  "Yeni Yedek Oluştur" butonuna tıklayarak ilk yedeğinizi oluşturun
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {backups.map((backup) => (
                  <div
                    key={backup.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-white/10 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="bg-primary/20 p-2 rounded-lg">
                        <Database className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-white font-medium">{backup.name}</p>
                        <div className="flex items-center space-x-2 mt-0.5">
                          <p className="text-xs text-slate-400">{backup.created_at}</p>
                          <Badge className="text-xs bg-slate-700 text-slate-300 border-slate-600">
                            {backup.size}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(backup.id)}
                        className="text-white border-white/10 hover:bg-blue-500/20 hover:border-blue-500/40"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        İndir
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setRestoreDialog(backup)}
                        className="text-white border-white/10 hover:bg-green-500/20 hover:border-green-500/40"
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Geri Yükle
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteDialog(backup)}
                        className="text-red-400 border-red-500/40 hover:bg-red-500/20"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Restore Dialog */}
        {restoreDialog && (
          <AlertDialog open={!!restoreDialog} onOpenChange={() => setRestoreDialog(null)}>
            <AlertDialogContent className="bg-slate-900 border-white/10">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-white">Veritabanını Geri Yükle?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400">
                  <strong className="text-yellow-400">{restoreDialog.name}</strong> yedeğinden geri yükleme yapılacak.
                  <br /><br />
                  <span className="text-red-400 font-semibold">UYARI:</span> Mevcut tüm veriler silinecek ve yedekteki verilerle değiştirilecek.
                  Bu işlem geri alınamaz!
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-slate-800 text-white border-white/10">İptal</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => handleRestore(restoreDialog.id)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Geri Yükle
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {/* Delete Dialog */}
        {deleteDialog && (
          <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
            <AlertDialogContent className="bg-slate-900 border-white/10">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-white">Yedeği Sil?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400">
                  <strong className="text-white">{deleteDialog.name}</strong> yedeği silinecek.
                  Bu işlem geri alınamaz!
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-slate-800 text-white border-white/10">İptal</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => handleDelete(deleteDialog.id)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Sil
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {/* Download Reminder Dialog */}
        {downloadReminderDialog && (
          <AlertDialog open={!!downloadReminderDialog} onOpenChange={() => setDownloadReminderDialog(null)}>
            <AlertDialogContent className="bg-slate-900 border-white/10">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-white flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-400" />
                  Yedeği İndirmeyi Unutmayın!
                </AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400">
                  <div className="space-y-3 mt-2">
                    <p>
                      <strong className="text-green-400">✓ Yedekleme başarıyla tamamlandı!</strong>
                    </p>
                    <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-lg p-3">
                      <p className="text-yellow-300 text-sm">
                        <strong>⚠️ Önemli:</strong> Yedekler sunucuda (MongoDB içinde) saklanıyor. 
                        Olası veri kaybına karşı yedeği bilgisayarınıza indirmeniz şiddetle önerilir!
                      </p>
                    </div>
                    <p className="text-sm text-slate-500">
                      Yedek: <span className="text-white font-mono">{downloadReminderDialog}</span>
                    </p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                <AlertDialogCancel className="bg-slate-800 text-white border-white/10">
                  Sonra İndir
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    handleDownload(downloadReminderDialog);
                    setDownloadReminderDialog(null);
                  }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Şimdi İndir
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
};

export default AdminBackup;
